import {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { NOMS_THEMES, THEME_PAR_DEFAUT, type NomTheme } from './themes.ts';

/**
 * Choix du thème.
 *
 * ── Deux états, et plus de troisième ────────────────────────────────────────
 * « Clair » ou « sombre ». L'entrée « Système » a été retirée, et son
 * comportement avec elle : l'application n'interroge plus
 * `prefers-color-scheme` et ouvre toujours en clair. C'est un choix explicite —
 * la même interface, pour tout le monde, tant que l'utilisateur n'en décide pas
 * autrement.
 *
 * Le choix est mémorisé PAR POSTE DE TRAVAIL, comme le repli de la barre
 * latérale : c'est un réglage de confort lié à l'écran qu'on a devant soi, et
 * il n'a pas à suivre l'enseignant de sa salle de classe à son ordinateur de la
 * maison.
 */

const CLE_STOCKAGE = 'journal.theme';

interface ContexteTheme {
  /** Thème appliqué. */
  theme: NomTheme;
  definir: (theme: NomTheme) => void;
}

const Contexte = createContext<ContexteTheme | null>(null);

function lireChoixEnregistre(): NomTheme {
  try {
    const brut = localStorage.getItem(CLE_STOCKAGE);
    return NOMS_THEMES.includes(brut as NomTheme) ? (brut as NomTheme) : THEME_PAR_DEFAUT;
  } catch {
    // Navigation privée, stockage refusé : le thème par défaut, sans bruit.
    return THEME_PAR_DEFAUT;
  }
}

export function FournisseurTheme({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<NomTheme>(lireChoixEnregistre);

  useEffect(() => {
    const racine = document.documentElement;
    /*
     * L'attribut est TOUJOURS posé, y compris pour le clair.
     *
     * On le retirait autrefois pour rendre la main à la requête média du
     * système. Celle-ci n'existe plus : le laisser absent reviendrait à
     * dépendre de l'ordre des règles CSS pour un état qui doit être explicite.
     */
    racine.setAttribute('data-theme', theme);
    // Indique au navigateur comment styler les éléments natifs — barres de
    // défilement, sélecteurs de date. Souvent oublié, très visible.
    racine.style.colorScheme = theme === 'dark' ? 'dark' : 'light';
  }, [theme]);

  const definir = useCallback((choisi: NomTheme) => {
    setTheme(choisi);
    try {
      localStorage.setItem(CLE_STOCKAGE, choisi);
    } catch {
      // Le thème s'applique quand même pour la session en cours.
    }
  }, []);

  const valeur = useMemo(() => ({ theme, definir }), [theme, definir]);
  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export function useTheme(): ContexteTheme {
  const contexte = useContext(Contexte);
  if (!contexte) throw new Error('useTheme doit être utilisé dans un FournisseurTheme');
  return contexte;
}
